<template>
  <div :class="panelRootClass">
    <component
      :is="embedded ? 'div' : 'vx-card'"
      v-if="isPageLoading"
      :class="embedded ? undefined : 'mb-6'"
    >
      <div class="py-8 text-center text-grey">กำลังโหลดข้อมูล...</div>
    </component>
    <component
      :is="embedded ? 'div' : 'vx-card'"
      v-else
      :class="embedded ? undefined : 'mb-6'"
    >
      <div :class="embedded ? undefined : 'pt-4'">
            <h4 class="mb-4">กฎเลือกช่องทางฝาก</h4>
            <p class="mb-4 text-sm text-grey">
              ลำดับการเช็คกฎ: <span class="text-danger">บนก่อน ล่างหลัง</span> — ลากที่ไอคอนขีดสามเส้นหรือกดลูกศรขึ้น/ลงเพื่อปรับ priority</p>
            <!-- payment_auto_failover: ค่ายแรกสร้างใบฝากไม่ได้ → เว็บผู้เล่นลองค่ายถัดไปตามลำดับกฎ (ค่าเริ่มต้นปิด) -->
            <div class="payment-auto-failover-box mb-4 p-3 rounded border border-solid d-theme-border-grey-light">
              <div class="flex flex-wrap items-center gap-3">
                <vs-switch
                  :key="paymentAutoFailoverSwitchKey"
                  :value="paymentAutoFailoverOn"
                  :disabled="updatingPaymentAutoFailover"
                  color="success"
                  @input="togglePaymentAutoFailover"
                />
                <span class="font-semibold">Payment Auto Failover</span>
                <span class="text-sm text-grey">— สลับค่ายฝากอัตโนมัติเมื่อค่ายสร้างรายการไม่สำเร็จ</span>
                <span :class="paymentAutoFailoverOn ? 'text-success' : 'text-grey'">({{ paymentAutoFailoverOn ? 'เปิด' : 'ปิด' }})</span>
              </div>
              <p class="text-sm text-grey mt-2 mb-2">
                เปิดแล้ว ถ้าค่ายแรกสร้างรายการไม่ได้ ระบบจะลองค่ายถัดไปที่ <b>เข้ากฎเดียวกัน</b> ตามลำดับบนลงล่าง (สูงสุด 10 ค่าย)
                — ต้องมีกฎสำรองที่เข้าเงื่อนไขด้วย เช่น กฎ priority 999 ที่ไม่ใส่เงื่อนไข
              </p>
              <!-- ทดสอบ: ยอดฝาก + (ไม่บังคับ) username จริง → กฎที่เข้า + ค่ายที่เว็บผู้เล่นจะลองจริง (อ่านอย่างเดียว ไม่สร้างใบ) -->
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm">ทดสอบยอดฝาก</span>
                <vs-input v-model="paymentAutoFailoverTestAmount" type="number" size="small" class="w-32" />
                <span class="text-sm">ยูสเซอร์</span>
                <vs-input
                  v-model="paymentRuleTestUsername"
                  size="small"
                  class="w-48"
                  placeholder="ไม่ใส่ = ดูแค่ยอดฝาก"
                  @keyup.enter="fetchPaymentAutoFailoverChain"
                />
                <vs-button size="small" type="border" :disabled="loadingPaymentAutoFailoverChain" @click="fetchPaymentAutoFailoverChain">
                  ดูลำดับ
                </vs-button>
              </div>
              <div v-if="paymentRuleTestResult" class="payment-rule-test-result text-sm mt-2">
                <div v-if="paymentRuleTestResult.username" class="text-grey">
                  {{ paymentRuleTestResult.username }}:
                  ฝากสะสม {{ formatRuleTestNumber(paymentRuleTestResult.context.total_deposit) }} บาท
                  • ฝาก {{ formatRuleTestNumber(paymentRuleTestResult.context.topup_count) }} ครั้ง
                  • ถอน {{ formatRuleTestNumber(paymentRuleTestResult.context.withdraw_count) }} ครั้ง
                  • {{ paymentRuleTestResult.context.is_new_user ? 'ยูสใหม่' : 'ยูสเก่า' }}
                  <span v-if="paymentRuleTestResult.context.bank_code">• ธนาคาร {{ paymentRuleTestResult.context.bank_code }}</span>
                </div>
                <div v-if="paymentRuleTestResult.matched_rule">
                  กฎแรกที่เข้า:
                  <b>#{{ paymentRuleTestResult.matched_rule.id }} {{ truncateText(paymentRuleTestResult.matched_rule.name || '', 60) }}</b>
                  (p{{ paymentRuleTestResult.matched_rule.priority }}) → <b>{{ (paymentRuleTestResult.matched_rule.payment_codes || [paymentRuleTestResult.matched_rule.payment_code]).join(' → ') }}</b>
                </div>
                <div v-else class="text-danger">ไม่มีกฎที่เข้าเงื่อนไข — ผู้เล่นจะฝากไม่ได้</div>
                <template v-if="paymentAutoFailoverChain && paymentAutoFailoverChain.length">
                  <div>
                    ค่ายที่เข้าทั้งหมด: {{ paymentAutoFailoverChain.join(' → ') }}
                    <span v-if="paymentAutoFailoverChain.length < 2" class="text-warning">(มีค่ายเดียว — ไม่มีตัวสำรอง)</span>
                  </div>
                  <div v-if="paymentRuleTestResult.effective_chain">
                    เว็บผู้เล่นจะลองจริงตอนนี้ (Payment Auto Failover {{ paymentRuleTestResult.failover_enabled ? 'เปิด' : 'ปิด' }}):
                    <b>{{ paymentRuleTestResult.effective_chain.join(' → ') }}</b>
                  </div>
                </template>
              </div>
            </div>
            <p v-if="paymentRulesActiveOnly" class="text-sm text-warning mb-2">
              ปิดตัวกรอง «แสดงเฉพาะที่เปิดใช้งาน» เพื่อจัดเรียงลำดับได้ครบทุกกฎ
            </p>
            <p v-if="isPaymentRulesDepositTypeFiltered" class="text-sm text-warning mb-2">
              เลือก «ทั้งหมด» ในตัวกรองประเภทฝาก เพื่อจัดเรียงลำดับได้ — priority เรียงรวมทุกประเภท
            </p>
            <div class="flex flex-wrap items-center gap-4 mb-4">
              <vs-checkbox v-model="paymentRulesActiveOnly">
                แสดงเฉพาะที่เปิดใช้งาน
              </vs-checkbox>
              <vs-button
                color="success"
                size="small"
                icon-pack="feather"
                icon="icon-plus"
                @click="openPaymentRuleAdd"
              >
                สร้าง rule
              </vs-button>
              <vs-button
                type="border"
                size="small"
                icon-pack="feather"
                icon="icon-refresh-cw"
                :disabled="loadingPaymentRules"
                @click="fetchPaymentRules"
              >
                รีเฟรช
              </vs-button>
            </div>
            <!-- ตัวกรองตามประเภทฝาก (deposit_type) — สร้างจากค่าที่มีจริงในข้อมูล -->
            <div
              v-if="depositTypeGroups.length > 1"
              class="flex flex-wrap items-center gap-2 mb-4"
            >
              <span class="text-sm text-grey mr-1">ประเภทฝาก:</span>
              <vs-button
                v-for="opt in depositTypeFilterOptions"
                :key="opt.value"
                :type="paymentRulesDepositTypeFilter === opt.value ? 'filled' : 'border'"
                :color="opt.color"
                size="small"
                @click="paymentRulesDepositTypeFilter = opt.value"
              >
                {{ opt.label }} ({{ opt.count }})
              </vs-button>
            </div>
            <vs-table stripe :data="visiblePaymentRules">
              <template slot="thead">
                <vs-th width="120">
                  <vx-tooltip text="ลากขีดสามเส้นหรือกดลูกศร — บนสุดถูกเช็คก่อน • กฎ priority 999 ล็อกท้าย ลากไม่ได้">
                    <span class="cursor-help">จัดลำดับ</span>
                  </vx-tooltip>
                </vs-th>
                <vs-th>ชื่อ</vs-th>
                <vs-th>
                  <vx-tooltip text="เรียงตามลำดับที่ลอง — ตัวแรกสร้างรายการไม่ได้ เปิด Payment Auto Failover ไว้จะลองตัวถัดไป">
                    <span class="cursor-help">ช่องทางรับเงิน</span>
                  </vx-tooltip>
                </vs-th>
                <vs-th>ประเภทฝาก</vs-th>
                <vs-th>สถานะ</vs-th>
                <vs-th>conditions</vs-th>
                <vs-th>อัปเดตล่าสุด</vs-th>
                <vs-th v-if="paymentRulesHaveUsage">usage</vs-th>
                <vs-th width="100">จัดการ</vs-th>
              </template>
              <template slot-scope="{ data }">
                <draggable
                  v-model="paymentRulesDraggableList"
                  tag="tbody"
                  :data-nrows="data.length"
                  :options="paymentRulesDraggableOptions"
                  :move="onPaymentRuleDragMove"
                  @end="onPaymentRuleDraggableEnd"
                >
                  <vs-tr
                    :key="tr.id"
                    v-for="(tr, trIdx) in visiblePaymentRules"
                    :class="{ 'payment-rule-tr--locked': isPaymentRulePriorityLocked(tr) }"
                  >
                  <vs-td :data="tr.priority">
                    <div class="flex items-center gap-1 flex-wrap">
                      <span
                        class="payment-rule-drag-handle inline-flex p-1 rounded select-none"
                        :class="{ 'opacity-50 pointer-events-none': isPaymentRuleReorderLocked || isPaymentRulePriorityLocked(tr) }"
                        :title="isPaymentRulePriorityLocked(tr) ? 'กฎ priority 999 ล็อกท้ายสุด — ลากไม่ได้' : 'คลิกค้างแล้วลากเพื่อสลับลำดับ'"
                      >
                        <feather-icon icon="MenuIcon" svgClasses="h-5 w-5 text-grey" />
                      </span>
                      <div class="flex flex-col payment-rule-order-btns">
                        <vs-button
                          type="line"
                          size="small"
                          icon-pack="feather"
                          icon="icon-chevron-up"
                          :disabled="!paymentRuleCanMoveUp(tr, trIdx)"
                          class="p-0 m-0"
                          style="min-height: 22px; height: 22px;"
                          @click="movePaymentRuleRow(tr, -1)"
                        />
                        <vs-button
                          type="line"
                          size="small"
                          icon-pack="feather"
                          icon="icon-chevron-down"
                          :disabled="!paymentRuleCanMoveDown(tr, trIdx)"
                          class="p-0 m-0"
                          style="min-height: 22px; height: 22px;"
                          @click="movePaymentRuleRow(tr, 1)"
                        />
                      </div>
                      <span class="text-sm text-grey ml-1">{{ tr.priority }}</span>
                      <span v-if="isPaymentRulePriorityLocked(tr)" class="text-xs text-warning ml-1">ล็อก</span>
                    </div>
                  </vs-td>
                  <vs-td :data="tr.name">
                    <vx-tooltip v-if="tr.name && String(tr.name).length > 48" :text="tr.name">
                      <span>{{ truncateText(tr.name, 48) }}</span>
                    </vx-tooltip>
                    <span v-else>{{ tr.name || '—' }}</span>
                    <!-- กฎที่ถูกกฎ priority น้อยกว่าบัง (คำนวณที่ API ตามลำดับที่เว็บผู้เล่นใช้จริง) -->
                    <vx-tooltip
                      v-if="paymentRuleShadowWarnings[tr.id]"
                      :text="paymentRuleShadowWarnings[tr.id].message"
                      class="payment-rule-shadow text-xs mt-1 cursor-help"
                      :class="'payment-rule-shadow--' + paymentRuleShadowWarnings[tr.id].level"
                    >
                      {{ paymentRuleShadowIcon(paymentRuleShadowWarnings[tr.id].level) }} {{ paymentRuleShadowWarnings[tr.id].label || paymentRuleShadowWarnings[tr.id].message }}
                    </vx-tooltip>
                  </vs-td>
                  <vs-td :data="tr.payment_code">
                    <div class="payment-rule-codes">
                      <span
                        v-for="(code, ci) in paymentRuleCodes(tr)"
                        :key="code"
                        class="payment-rule-code-chip"
                        :class="{ 'payment-rule-code-chip--first': ci === 0 }"
                        :title="getPaymentLabelByCode(code)"
                      >
                        <span v-if="paymentRuleCodes(tr).length > 1" class="payment-rule-code-chip__no">{{ ci + 1 }}</span>{{ code }}
                      </span>
                    </div>
                  </vs-td>
                  <vs-td :data="tr.deposit_type">
                    <span class="deposit-type-tag" :class="depositTypeTextClass(tr.deposit_type)">
                      <span class="deposit-type-tag__dot" />{{ depositTypeShortLabel(tr.deposit_type) }}
                    </span>
                  </vs-td>
                  <vs-td :data="tr.is_active">
                    <vs-switch v-model="tr.is_active" @click="togglePaymentRuleActive(tr)">
                      <span slot="on">เปิด</span>
                      <span slot="off">ปิด</span>
                    </vs-switch>
                  </vs-td>
                  <vs-td>
                    <div class="text-xs">
                      <pre
                        v-if="expandedConditions[String(tr.id)]"
                        class="conditions-json p-2 rounded max-h-48 overflow-auto text-left border border-solid d-theme-border-grey-light d-theme-dark-bg"
                      >{{ formatConditionsJson(tr.conditions) }}</pre>
                      <span v-else class="text-grey">{{ tr.description || conditionsPreview(tr) }}</span>
                      <vs-button
                        type="line"
                        size="small"
                        class="mt-1"
                        @click="toggleConditionsExpand(tr.id)"
                      >
                        {{ expandedConditions[String(tr.id)] ? 'ย่อ' : 'ขยาย JSON' }}
                      </vs-button>
                    </div>
                  </vs-td>
                  <vs-td :data="tr.updated_at">{{ formatDateTime(tr.updated_at) }}</vs-td>
                  <vs-td v-if="paymentRulesHaveUsage">
                    <span v-if="tr.usage_limit != null">{{ (tr.used_count != null ? tr.used_count : '—') }} / {{ tr.usage_limit }}</span>
                    <span v-else>—</span>
                  </vs-td>
                  <vs-td>
                    <div class="flex items-center flex-no-wrap">
                      <vx-tooltip text="แก้ไข">
                        <vs-button
                          radius
                          type="border"
                          size="small"
                          icon-pack="feather"
                          icon="icon-edit"
                          color="warning"
                          @click="openPaymentRuleEdit(tr)"
                        />
                      </vx-tooltip>
                      <vx-tooltip text="ลบ" class="ml-2">
                        <vs-button
                          radius
                          type="border"
                          size="small"
                          icon-pack="feather"
                          icon="icon-trash-2"
                          color="danger"
                          @click="openPaymentRuleDelete(tr)"
                        />
                      </vx-tooltip>
                    </div>
                  </vs-td>
                </vs-tr>
                </draggable>
              </template>
            </vs-table>
            <div v-if="visiblePaymentRules.length === 0 && !loadingPaymentRules" class="text-center py-8 text-grey">
              <span v-if="paymentRules.length > 0">
                ไม่มีกฎในประเภทฝาก «{{ depositTypeLabel(paymentRulesDepositTypeFilter) }}»
              </span>
              <span v-else>ยังไม่มี payment rules — กด «สร้าง rule»</span>
            </div>
            <div v-if="loadingPaymentRules" class="text-center py-4 text-grey">กำลังโหลด...</div>
      </div>
    </component>
    <!-- Modal สร้าง/แก้ไข payment rule -->
    <vs-popup
      :title="paymentRuleFormMode === 'add' ? 'สร้างกฎช่องทางฝาก' : 'แก้ไขกฎช่องทางฝาก'"
      :active.sync="showPaymentRuleModal"
      classContent="popup-example popup-payment-rule-wide"
      @close="resetPaymentRuleForm"
    >
      <div class="vx-row">
        <div class="vx-col w-full flex justify-end mb-2">
          <vx-tooltip
            title="ใช้หน้านี้อย่างไร (กดตามลำดับ)"
            :text="paymentRuleModalHowToText"
            position="left"
            color="dark"
          >
            <span class="cursor-help inline-flex items-center payment-rule-howto-trigger" tabindex="0" aria-label="คำแนะนำการใช้หน้านี้">
              <feather-icon icon="HelpCircleIcon" svgClasses="h-6 w-6 text-grey hover:text-primary" />
            </span>
          </vx-tooltip>
        </div>
        <div class="vx-col w-full mb-3">
          <vs-input
            v-model="paymentRuleForm.name"
            label="ชื่อที่แสดง"
            placeholder="เว้นว่าง = ใช้ชื่อที่ระบบกำหนดจากเงื่อนไข"
            class="w-full"
          />
          <p class="text-xs text-grey mt-1 mb-0">
            <template>
              ถ้าไม่ระบุ ระบบจะตั้งชื่อจากเงื่อนไขโดยอัตโนมัติ
            </template>
          </p>
        </div>
        <div class="vx-col w-full mb-2">
          <p class="text-sm text-grey mb-0">ลำดับการเช็คกฎ: จัดในตารางด้านบน — ลากไอคอนขีดสามเส้นหรือกดลูกศรขึ้น/ลง</p>
        </div>
        <div class="vx-col w-full mb-3">
          <p class="text-base font-semibold mb-1">ลูกค้าต้องเป็นแบบไหน ถึงจะเข้ากฎนี้</p>
          <p class="text-sm text-grey mb-3">เลือกว่าจะเช็คจากข้อมูลอะไร แล้วใส่ค่า — ทำทีละข้อได้</p>
          <div class="flex flex-wrap items-center gap-2 mb-3">
            <vs-button
              :type="paymentRuleConditionsEditor === 'builder' ? 'filled' : 'border'"
              size="small"
              color="primary"
              @click="setPaymentRuleConditionsEditor('builder')"
            >
              แบบกดเลือก (แนะนำ)
            </vs-button>
            <vs-button
              :type="paymentRuleConditionsEditor === 'json' ? 'filled' : 'border'"
              size="small"
              color="dark"
              @click="setPaymentRuleConditionsEditor('json')"
            >
              โหมดผู้ดูแล (แก้เป็น JSON)
            </vs-button>
            <vs-button
              v-if="paymentRuleConditionsEditor === 'builder'"
              type="line"
              size="small"
              icon-pack="feather"
              icon="icon-refresh-cw"
              :disabled="loadingPaymentRuleContextKeys"
              @click="fetchPaymentRuleContextKeys"
            >
              โหลดรายการเรื่องที่เช็คได้ใหม่
            </vs-button>
          </div>
          <p v-if="builderHydrateFailed" class="text-sm text-warning mb-2">
            เงื่อนไขชุดนี้ซับซ้อนเกินกว่าฟอร์มจะแสดง — ใช้โหมดผู้ดูแล (JSON) แก้ต่อ หรือให้ผู้เชี่ยวชาญช่วย
          </p>
          <div v-show="paymentRuleConditionsEditor === 'builder'" class="condition-builder border border-solid d-theme-border-grey-light rounded p-3 mb-3 d-theme-dark-light-bg">
            <p v-if="loadingPaymentRuleContextKeys" class="text-sm text-grey mb-2">กำลังโหลดรายการเรื่องที่เลือกได้...</p>
            <p v-else-if="sortedPaymentRuleContextKeys.length === 0" class="text-sm text-warning mb-2">
              ยังไม่มีรายการเรื่องให้เลือก — ติดต่อทีมระบบ หรือใช้โหมดผู้ดูแล (JSON)
            </p>
            <p v-else class="text-sm text-grey mb-3">
              เลือกว่า หลายข้อต้องตรงพร้อมกัน หรือ แค่ข้อใดข้อหนึ่งตรงก็พอ
            </p>
            <div class="mb-3">
              <vs-select
                v-model="conditionBuilderRoot"
                label="รวมเงื่อนไขแบบไหน"
                class="w-full max-w-lg"
              >
                <vs-select-item value="AND" text="ทุกข้อต้องตรงกันหมด (และ)" />
                <vs-select-item value="OR" text="แค่ข้อใดข้อหนึ่งตรงก็พอ (หรือ)" />
              </vs-select>
            </div>
            <div
              v-for="(row, idx) in conditionBuilderRows"
              :key="row._uid"
              class="condition-row mb-3 p-3 rounded border border-solid d-theme-border-grey-light d-theme-dark-bg"
            >
              <div class="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div>
                  <span class="text-sm font-semibold">ข้อที่ {{ idx + 1 }}</span>
                  <p class="text-xs text-grey mb-0">เลือกเรื่องที่จะเช็ค แล้วใส่ค่า — ช่องไหนไม่ใช้เว้นว่างได้</p>
                </div>
                <div class="flex flex-wrap gap-1">
                  <vs-button
                    type="line"
                    size="small"
                    icon-pack="feather"
                    icon="icon-chevrons-up"
                    title="เลื่อนขึ้น"
                    :disabled="idx === 0"
                    @click="moveConditionRow(idx, -1)"
                  />
                  <vs-button
                    type="line"
                    size="small"
                    icon-pack="feather"
                    icon="icon-chevrons-down"
                    title="เลื่อนลง"
                    :disabled="idx >= conditionBuilderRows.length - 1"
                    @click="moveConditionRow(idx, 1)"
                  />
                  <vs-button
                    type="line"
                    size="small"
                    color="danger"
                    icon-pack="feather"
                    icon="icon-trash"
                    title="ลบข้อนี้"
                    @click="removeConditionRow(idx)"
                  />
                </div>
              </div>
              <div class="vx-row">
                <div class="vx-col w-full mb-2">
                  <vs-select
                    autocomplete
                    v-model="row.context_key"
                    label="จะเช็คจากเรื่องไหน"
                    class="w-full"
                    placeholder="กดเลือก เช่น ยอดฝากสะสม, ธนาคารของลูกค้า"
                  >
                    <vs-select-item
                      v-for="ck in sortedPaymentRuleContextKeys"
                      :key="ck.context_key"
                      :value="ck.context_key"
                      :text="contextKeyOptionText(ck)"
                    />
                  </vs-select>
                  <p v-if="contextMetaForRow(row)" class="text-xs text-grey mt-1">
                    {{ contextMetaForRow(row).description_th || '' }}
                  </p>
                </div>
                <template v-if="row.context_key && contextMetaForRow(row)">
                  <div v-if="contextMetaForRow(row).value_type === 'number'" class="vx-col w-full">
                    <p class="text-sm mb-2">ใส่ตัวเลขตามที่ต้องการ (ไม่ใช้ช่องไหนเว้นว่าง)</p>
                    <div class="flex flex-wrap gap-2">
                      <vs-input
                        v-model="row.num_gte"
                        type="text"
                        inputmode="decimal"
                        label="ไม่ต่ำกว่า (ขั้นต่ำ)"
                        class="w-36"
                        @blur="row.num_gte = formatNumberFieldOnBlur(row.num_gte)"
                      />
                      <vs-input
                        v-model="row.num_lte"
                        type="text"
                        inputmode="decimal"
                        label="ไม่เกิน (สูงสุด)"
                        class="w-36"
                        @blur="row.num_lte = formatNumberFieldOnBlur(row.num_lte)"
                      />
                      <!-- <vs-input v-model="row.num_gt" type="number" label="มากกว่า" class="w-32" />
                      <vs-input v-model="row.num_lt" type="number" label="น้อยกว่า" class="w-32" /> -->
                      <!-- <vs-input v-model="row.num_eq" type="number" label="เท่ากับพอดี" class="w-32" /> -->
                    </div>
                    <div
                      v-for="(np, npIdx) in numberPreviewWrap(row)"
                      :key="row._uid + '-numprev-' + npIdx"
                      class="condition-preview-live mt-3 p-3 rounded border border-solid d-theme-border-grey-light d-theme-dark-bg"
                    >
                      <p class="text-xs text-grey mb-1">ตัวอย่างสด (อัปเดตทันทีที่กรอก)</p>
                      <p
                        v-if="np.rangeLine"
                        class="text-xl font-semibold mb-1 condition-preview-range"
                      >
                        {{ np.rangeLine }}
                      </p>
                      <p class="text-sm mb-0 condition-preview-detail">
                        {{ np.detail }}
                      </p>
                    </div>
                  </div>
                  <div v-else-if="contextMetaForRow(row).value_type === 'boolean'" class="vx-col w-full mb-2">
                    <p class="text-sm text-grey mb-0">
                      ใช้เมื่อค่านี้เป็น ใช่ เท่านั้น — ระบบกำหนดให้อัตโนมัติ (ไม่ต้องเลือก true/false)
                    </p>
                  </div>
                  <div v-else-if="row.context_key === 'bank_code'" class="vx-col w-full mb-2">
                    <vs-select
                      autocomplete
                      v-model="row.bank_preset"
                      label="ธนาคารที่ลูกค้าใช้"
                      class="w-full max-w-md"
                      placeholder="กดเลือกธนาคาร"
                    >
                      <vs-select-item
                        v-for="b in ruleBankCodeOptions"
                        :key="b.value"
                        :value="b.value"
                        :text="b.label"
                      />
                    </vs-select>
                    <vs-input
                      v-if="row.bank_preset === '__other__'"
                      v-model="row.bank_other"
                      label="พิมพ์รหัสธนาคารเอง (เช่น SCB)"
                      class="w-full mt-2"
                    />
                  </div>
                  <div v-else class="vx-col w-full mb-2">
                    <vs-input
                      v-model="row.str_value"
                      label="ค่าที่ต้องตรงกัน"
                      class="w-full"
                    />
                  </div>
                </template>
              </div>
            </div>
            <vs-button
              color="success"
              type="border"
              size="small"
              icon-pack="feather"
              icon="icon-plus"
              @click="addConditionRow"
            >
              เพิ่มเงื่อนไขอีกข้อ
            </vs-button>
          </div>
          <div v-show="paymentRuleConditionsEditor === 'json'">
            <label class="vs-input--label">ข้อมูลเงื่อนไขแบบข้อความ (JSON)</label>
            <p class="text-xs text-grey mb-2">สำหรับผู้ที่คุ้นเคยรูปแบบนี้เท่านั้น — พนักงานทั่วไปแนะนำใช้ปุ่ม «แบบกดเลือก»</p>
            <textarea
              v-model="paymentRuleForm.conditionsText"
              class="payment-rule-conditions-textarea w-full border border-solid d-theme-border-grey-light rounded p-2 font-mono text-sm d-theme-dark-bg"
              rows="12"
              placeholder="เช่น { &quot;AND&quot;: [ { &quot;total_deposit&quot;: { &quot;gte&quot;: 1, &quot;lte&quot;: 99 } } ] }"
            />
          </div>
          <span v-if="paymentRuleFormErrors.conditions" class="text-danger text-sm">{{ paymentRuleFormErrors.conditions }}</span>
          <!-- <p class="text-xs text-grey mt-1">
            แบบกดเลือกรองรับ «ทุกข้อตรง» หรือ «ข้อใดข้อหนึ่งตรง» ชั้นเดียว — ถ้าเงื่อนไขซ้อนหลายชั้น ให้ใช้โหมดผู้ดูแล (JSON)
          </p> -->
        </div>
        <div class="vx-col w-full mb-3">
          <!-- กฎ 1 ข้อหลายค่าย: ตัวแรกใช้ก่อน สร้างรายการไม่ได้ + เปิด Payment Auto Failover = ลองตัวถัดไป -->
          <label class="vs-input--label">ช่องทางรับเงิน (เรียงตามลำดับที่ลอง)</label>
          <div
            v-for="(code, ci) in paymentRuleForm.payment_codes"
            :key="code"
            class="payment-rule-form-code flex items-center mb-2 p-2 rounded border border-solid d-theme-border-grey-light"
          >
            <span class="payment-rule-code-chip__no mr-2">{{ ci + 1 }}</span>
            <span class="flex-1">
              {{ getPaymentLabelByCode(code) }}
              <span class="text-xs text-grey ml-1">({{ code }})</span>
              <span v-if="ci === 0" class="text-xs text-success ml-1">ใช้ก่อน</span>
              <span v-else class="text-xs text-grey ml-1">สำรอง</span>
            </span>
            <vs-button
              type="flat"
              size="small"
              icon-pack="feather"
              icon="icon-chevron-up"
              :disabled="ci === 0"
              @click="movePaymentRuleFormCode(ci, -1)"
            />
            <vs-button
              type="flat"
              size="small"
              icon-pack="feather"
              icon="icon-chevron-down"
              :disabled="ci === paymentRuleForm.payment_codes.length - 1"
              @click="movePaymentRuleFormCode(ci, 1)"
            />
            <vs-button
              type="flat"
              size="small"
              color="danger"
              icon-pack="feather"
              icon="icon-x"
              @click="removePaymentRuleFormCode(ci)"
            />
          </div>
          <vs-select
            v-if="paymentRuleFormCodeOptions.length > 0"
            :key="'add-code-' + paymentRuleForm.payment_codes.length"
            autocomplete
            :value="''"
            class="w-full"
            :placeholder="paymentRuleForm.payment_codes.length ? '+ เพิ่มช่องทางสำรอง' : 'กดเลือกช่องทาง'"
            @input="addPaymentRuleFormCode"
          >
            <vs-select-item
              v-for="(item, i) in paymentRuleFormCodeOptions"
              :key="item.id || i"
              :value="item.code"
              :text="item.name_th || item.name_en || item.code"
            />
          </vs-select>
          <p v-if="paymentRuleForm.payment_codes.length > 1" class="text-xs text-grey mt-1 mb-0">
            ค่ายสำรองจะถูกลองเฉพาะตอนเปิด Payment Auto Failover
          </p>
          <span v-if="paymentRuleFormErrors.payment_code" class="text-danger text-sm">{{ paymentRuleFormErrors.payment_code }}</span>
        </div>
        <div class="vx-col w-full mb-3">
          <vs-checkbox v-model="paymentRuleForm.is_active">เปิดใช้งานกฎนี้</vs-checkbox>
        </div>
      </div>
      <div
        v-if="showPaymentRuleModal && paymentRulePreSubmitSummary.show"
        class="payment-rule-pre-summary mb-4 p-3 rounded border border-solid d-theme-border-grey-light d-theme-dark-light-bg"
      >
        <p class="text-xs text-grey mb-1">สรุปเงื่อนไข</p>
        <p class="text-base leading-normal mb-2 payment-rule-pre-summary-line">
          {{ paymentRulePreSubmitSummary.line }}
        </p>
        <p v-if="paymentRuleForm.payment_codes.length" class="text-sm mb-0">
          <span class="text-grey">ช่องทางการฝาก:</span>
          <span class="ml-1">{{ paymentRuleForm.payment_codes.map(getPaymentLabelByCode).join(' → ') }}</span>
        </p>
        <p v-else class="text-xs text-warning mb-0">
          ยังไม่ได้เลือกช่องทางรับเงิน
        </p>
        <p v-if="paymentRulePreSubmitSummary.incomplete > 0" class="text-xs text-warning mt-2 mb-0">
          มีข้อที่ยังกรอกไม่ครบ — ตรวจอีกครั้งก่อนบันทึก
        </p>
      </div>
      <div class="flex justify-end mt-4">
        <vs-button type="border" @click="showPaymentRuleModal = false">ยกเลิก</vs-button>
        <vs-button
          class="ml-2"
          :disabled="savingPaymentRule || loadingPaymentRuleDetail"
          @click="submitPaymentRuleForm"
        >
          {{ savingPaymentRule ? 'กำลังบันทึก...' : (paymentRuleFormMode === 'add' ? 'สร้างกฎ' : 'บันทึกการแก้ไข') }}
        </vs-button>
      </div>
    </vs-popup>
    <!-- Modal ยืนยันลบ payment rule -->
    <vs-popup
      title="ยืนยันการลบกฎ"
      :active.sync="showPaymentRuleDeleteModal"
      classContent="popup-example"
    >
      <p v-if="paymentRuleDeleteTarget" class="mb-2">
        ลบกฎ <b>#{{ paymentRuleDeleteTarget.id }}</b> (p{{ paymentRuleDeleteTarget.priority }} {{ paymentRuleCodes(paymentRuleDeleteTarget).join(' → ') }})
        {{ paymentRuleDeleteTarget.name }} ใช่หรือไม่?
      </p>
      <p class="text-sm text-grey mb-4">ลบแล้วกู้คืนจากหน้านี้ไม่ได้ ถ้าจะปิดชั่วคราวให้กดแก้ไขแล้วปิดสถานะแทน</p>
      <div class="flex justify-end">
        <vs-button type="border" @click="showPaymentRuleDeleteModal = false">ยกเลิก</vs-button>
        <vs-button color="danger" class="ml-2" :disabled="deletingPaymentRule" @click="confirmPaymentRuleDelete">ยืนยันลบ</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import {
  getBankLists,
  getPaymentRules,
  getPaymentRule,
  createPaymentRule,
  updatePaymentRule,
  deletePaymentRule,
  getPaymentRuleContextKeys,
  previewPaymentRules,
  getPaymentAutoFailoverStatus,
  updatePaymentAutoFailoverStatus
} from '@/api/settingPayment'
import draggable from 'vuedraggable'

/** กฎที่ priority = ค่านี้ถือว่าล็อกท้ายสุด — ลาก/เลื่อนไม่ได้ และกฎอื่นจะได้ priority เป็น 0…เท่านั้น (ไม่ถึง 999) */
const PAYMENT_RULE_LOCK_PRIORITY = 999
/** ส่งทุกครั้งตอนสร้างกฎใหม่ — อยู่ก่อนกฎล็อก 999 แล้วจัดลำดับในตารางได้ */
const PAYMENT_RULE_DEFAULT_CREATE_PRIORITY = 998

/** ค่าตัวกรองประเภทฝากที่หมายถึง «ไม่กรอง» */
const DEPOSIT_TYPE_ALL = 'all'
/** ค่าที่ใช้แทนกฎที่ API ไม่ได้ส่ง deposit_type มา */
const DEPOSIT_TYPE_UNKNOWN = '__none__'
/**
 * ป้าย/สีของ deposit_type ที่รู้จัก — ค่าอื่นที่ API เพิ่มมาใหม่จะแสดงตามค่าดิบเอง
 * label = ปุ่มตัวกรอง (ยาวได้) • short = ในตาราง (สั้นให้แถวไม่แน่น)
 */
const DEPOSIT_TYPE_META = {
  fiat: { label: 'เงินบาท (fiat)', short: 'เงินบาท', color: 'primary', textClass: 'text-primary' },
  crypto: { label: 'คริปโต (crypto)', short: 'คริปโต', color: 'warning', textClass: 'text-warning' },
  [DEPOSIT_TYPE_UNKNOWN]: { label: 'ไม่ระบุ', short: 'ไม่ระบุ', color: 'dark', textClass: 'text-grey' }
}

// รหัสธนาคารสำหรับเงื่อนไข bank_code ใน rule engine (สตริง เช่น SCB — ไม่ใช่รหัส 3 หลัก)
const RULE_BANK_CODE_OPTIONS = [
  { label: 'ไทยพาณิชย์ (SCB)', value: 'SCB' },
  { label: 'กสิกรไทย (KBANK)', value: 'KBANK' },
  { label: 'กรุงไทย (KTB)', value: 'KTB' },
  { label: 'กรุงศรีอยุธยา (BAY)', value: 'BAY' },
  { label: 'กรุงเทพ (BBL)', value: 'BBL' },
  { label: 'ทหารไทยธนชาต (TTB)', value: 'TTB' },
  { label: 'ออมสิน (GSB)', value: 'GSB' },
  { label: 'เกียรตินาคิน (KKP)', value: 'KKP' },
  { label: 'ซีไอเอ็มบีไทย (CIMB)', value: 'CIMB' },
  { label: 'ยูโอบี (UOB)', value: 'UOB' },
  { label: 'อื่น ๆ (พิมพ์เอง)', value: '__other__' }
]

export default {
  name: 'PaymentRulesPanel',
  components: { draggable },
  props: {
    /** ใช้ภายใน vs-tab ของหน้าอื่น — ไม่ใส่ vx-card ซ้ำ / ไม่ใส่ margin แบบหน้าเต็ม */
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      paymentAutoFailoverOn: false,
      paymentAutoFailoverSwitchKey: 0,
      updatingPaymentAutoFailover: false,
      paymentAutoFailoverTestAmount: 100,
      paymentAutoFailoverChain: null,
      paymentRuleTestUsername: '',
      paymentRuleTestResult: null,
      paymentRuleShadowWarnings: {},
      showPaymentRuleDeleteModal: false,
      paymentRuleDeleteTarget: null,
      deletingPaymentRule: false,
      loadingPaymentAutoFailoverChain: false,

      isPageLoading: true,
      bankLists: [],
      paymentRules: [],
      loadingPaymentRules: false,
      paymentRulesActiveOnly: false,
      // กรองฝั่ง client ตาม deposit_type — 'all' = ไม่กรอง
      paymentRulesDepositTypeFilter: DEPOSIT_TYPE_ALL,
      expandedConditions: {},
      showPaymentRuleModal: false,
      paymentRuleFormMode: 'add',
      savingPaymentRule: false,
      loadingPaymentRuleDetail: false,
      paymentRuleForm: {
        id: null,
        name: '',
        payment_codes: [],
        is_active: true,
        conditionsText: '{}'
      },
      reorderingPaymentRules: false,
      paymentRuleFormErrors: {},
      paymentRuleConditionsEditor: 'builder',
      conditionBuilderRoot: 'AND',
      conditionBuilderRows: [],
      paymentRuleContextKeys: [],
      loadingPaymentRuleContextKeys: false,
      builderHydrateFailed: false,
      ruleBankCodeOptions: RULE_BANK_CODE_OPTIONS,
      paymentRuleModalHowToText:
        '1) กำหนดลูกค้าแบบไหนถึงจะเข้ากฎ — กดเพิ่มเงื่อนไขทีละข้อ แล้วเลือกเรื่องที่จะเช็ค (เช่น ยอดฝาก ยอดสะสม อื่นๆ) • \n' +
        '2) เลือกช่องทางรับเงินที่ลูกค้าจะได้ใช้เมื่อเข้าเงื่อนไข — เพิ่มได้หลายค่าย เรียงลำดับได้ ตัวแรกใช้ก่อน ที่เหลือเป็นค่ายสำรองตอนเปิด Payment Auto Failover • \n' +
        '3) เปิด/ปิดใช้งานกฎตามต้องการ แล้วกดสร้างหรือบันทึก • \n'

    }
  },
  computed: {
    /** ช่องทางที่ยังไม่ได้เลือกในกฎที่กำลังแก้ */
    paymentRuleFormCodeOptions () {
      const picked = this.paymentRuleForm.payment_codes || []
      return (this.bankLists || []).filter(item => !picked.includes(item.code))
    },
    panelRootClass () {
      if (this.embedded) {
        return 'payment-rules-panel payment-rules-panel--embedded'
      }
      return 'payment-rules-panel tab-general md:ml-4 md:mt-0 mt-4 ml-0'
    },
    /** ประเภทฝากที่มีอยู่จริงในข้อมูล + จำนวนกฎของแต่ละประเภท (เรียงมากไปน้อย) */
    depositTypeGroups () {
      const counter = {}
      const order = []
      ;(this.paymentRules || []).forEach((r) => {
        const key = this.depositTypeKey(r && r.deposit_type)
        if (counter[key] === undefined) {
          counter[key] = 0
          order.push(key)
        }
        counter[key]++
      })
      return order
        .map(key => ({
          value: key,
          label: this.depositTypeLabel(key),
          color: this.depositTypeColor(key),
          count: counter[key]
        }))
        .sort((a, b) => (b.count - a.count) || a.value.localeCompare(b.value))
    },
    depositTypeFilterOptions () {
      return [
        {
          value: DEPOSIT_TYPE_ALL,
          label: 'ทั้งหมด',
          color: 'dark',
          count: (this.paymentRules || []).length
        },
        ...this.depositTypeGroups
      ]
    },
    isPaymentRulesDepositTypeFiltered () {
      return this.paymentRulesDepositTypeFilter !== DEPOSIT_TYPE_ALL
    },
    /** ห้ามจัดลำดับเมื่อรายการที่เห็นไม่ใช่ทั้งหมด — priority คิดจากตำแหน่งในลิสต์เต็ม */
    isPaymentRuleReorderLocked () {
      return this.paymentRulesActiveOnly || this.reorderingPaymentRules || this.isPaymentRulesDepositTypeFiltered
    },
    visiblePaymentRules () {
      if (!this.isPaymentRulesDepositTypeFiltered) return this.paymentRules || []
      const target = this.paymentRulesDepositTypeFilter
      return (this.paymentRules || []).filter(r => this.depositTypeKey(r && r.deposit_type) === target)
    },
    /** v-model ของ draggable — เขียนกลับได้เฉพาะตอนไม่กรอง (ตอนกรองการลากถูกปิดอยู่) */
    paymentRulesDraggableList: {
      get () {
        return this.visiblePaymentRules
      },
      set (val) {
        if (this.isPaymentRulesDepositTypeFiltered) return
        this.paymentRules = val
      }
    },
    paymentRulesHaveUsage () {
      return (this.paymentRules || []).some(
        r => r && ((r.usage_limit !== undefined && r.usage_limit !== null) || (r.used_count !== undefined && r.used_count !== null))
      )
    },
    sortedPaymentRuleContextKeys () {
      const list = (this.paymentRuleContextKeys || []).filter(c => c && c.is_active !== false)
      return [...list].sort((a, b) => {
        const oa = (a.sort_order !== undefined && a.sort_order !== null) ? Number(a.sort_order) : 0
        const ob = (b.sort_order !== undefined && b.sort_order !== null) ? Number(b.sort_order) : 0
        if (oa !== ob) return oa - ob
        return String(a.context_key || '').localeCompare(String(b.context_key || ''))
      })
    },
    paymentRulePreSubmitSummary () {
      if (!this.showPaymentRuleModal) {
        return { show: false, line: '', incomplete: 0 }
      }
      if (this.paymentRuleConditionsEditor === 'builder') {
        return this.humanSummaryFromBuilderState()
      }
      const parsed = this.parseConditionsObject()
      if (parsed.ok && parsed.value && typeof parsed.value === 'object' && !Array.isArray(parsed.value) && Object.keys(parsed.value).length > 0) {
        const line = this.humanSummaryFromConditionsObject(parsed.value)
        if (line) {
          return { show: true, line, incomplete: 0 }
        }
      }
      return { show: false, line: '', incomplete: 0 }
    },
    paymentRulesDraggableOptions () {
      return {
        handle: '.payment-rule-drag-handle',
        animation: 150,
        disabled: this.isPaymentRuleReorderLocked
      }
    },
    /** สำหรับข้อความช่วย — สรุปจากเงื่อนไขในฟอร์ม (เมื่อไม่ระบุชื่อเอง) */
    paymentRuleSystemDefaultNamePreview () {
      if (!this.showPaymentRuleModal) return ''
      const sum = this.paymentRulePreSubmitSummary
      if (sum && sum.show && sum.line) {
        return String(sum.line).trim()
      }
      return ''
    }
  },
  async mounted () {
    try {
      await Promise.all([
        this.fetchBankLists(),
        this.fetchPaymentRules(),
        this.fetchPaymentRuleContextKeys(),
        this.fetchPaymentAutoFailoverStatus()
      ])
    } finally {
      this.isPageLoading = false
    }
  },
  watch: {
    paymentRulesActiveOnly () {
      this.fetchPaymentRules()
    },
    /** ถ้าประเภทที่กรองอยู่หายไปหลังโหลดใหม่ ให้กลับไป «ทั้งหมด» ไม่ให้ตารางว่างค้าง */
    paymentRules () {
      if (!this.isPaymentRulesDepositTypeFiltered) return
      const stillExists = this.depositTypeGroups.some(g => g.value === this.paymentRulesDepositTypeFilter)
      if (!stillExists) this.paymentRulesDepositTypeFilter = DEPOSIT_TYPE_ALL
    }
  },
  methods: {
    async fetchPaymentAutoFailoverStatus () {
      try {
        const res = await getPaymentAutoFailoverStatus()
        this.paymentAutoFailoverOn = !!(res && res.setting_status)
      } catch (_) {
        this.paymentAutoFailoverOn = false
      }
    },
    async togglePaymentAutoFailover (value) {
      const target = !!value
      if (target && !window.confirm('เปิด Payment Auto Failover? ผู้เล่นจะได้รายการจากค่ายสำรองเมื่อค่ายแรกสร้างรายการไม่ได้')) {
        this.paymentAutoFailoverSwitchKey += 1 // ยกเลิก → วาด switch ใหม่ให้กลับเป็นสถานะจริง
        return
      }
      this.updatingPaymentAutoFailover = true
      try {
        const res = await updatePaymentAutoFailoverStatus(target)
        if (res && res.status === true) {
          this.paymentAutoFailoverOn = !!res.setting_status
          this.$vs.notify({
            time: 3000,
            color: this.paymentAutoFailoverOn ? 'success' : 'warning',
            position: 'top-right',
            title: this.paymentAutoFailoverOn ? 'เปิด' : 'ปิด',
            text: 'Payment Auto Failover'
          })
        } else {
          this.showError(new Error(res && res.message ? res.message : 'อัปเดตไม่สำเร็จ'))
        }
      } catch (err) {
        this.showError(err)
      } finally {
        await this.fetchPaymentAutoFailoverStatus()
        this.paymentAutoFailoverSwitchKey += 1
        this.updatingPaymentAutoFailover = false
      }
    },
    openPaymentRuleDelete (tr) {
      this.paymentRuleDeleteTarget = tr
      this.showPaymentRuleDeleteModal = true
    },
    async confirmPaymentRuleDelete () {
      if (!this.paymentRuleDeleteTarget) return
      this.deletingPaymentRule = true
      try {
        const res = await deletePaymentRule(this.paymentRuleDeleteTarget.id)
        if (!res || res.status !== true) {
          this.showError(new Error((res && res.message) || 'ลบกฎไม่สำเร็จ'))
          return
        }
        this.$vs.notify({ color: 'success', title: 'ลบกฎสำเร็จ', position: 'top-right' })
        this.showPaymentRuleDeleteModal = false
        this.paymentRuleDeleteTarget = null
        await this.fetchPaymentRules()
      } catch (err) {
        this.showError(err)
      } finally {
        this.deletingPaymentRule = false
      }
    },
    paymentRuleShadowIcon (level) {
      if (level === 'shadowed') return '⛔'
      if (level === 'backup') return '↳'
      return '⚠️'
    },
    formatRuleTestNumber (val) {
      const n = Number(val)
      if (isNaN(n)) return '—'
      return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
    },
    async fetchPaymentAutoFailoverChain () {
      const amount = Number(this.paymentAutoFailoverTestAmount)
      if (!amount || amount <= 0) return
      this.loadingPaymentAutoFailoverChain = true
      try {
        const body = { deposit_amount: amount }
        const username = String(this.paymentRuleTestUsername || '').trim()
        if (username) body.username = username
        const res = await previewPaymentRules(body)
        if (res && res.status) {
          this.paymentAutoFailoverChain = Array.isArray(res.failover_chain) ? res.failover_chain : []
          this.paymentRuleTestResult = {
            username: username ? (res.context && res.context.username) || username : '',
            context: res.context || {},
            matched_rule: res.matched_rule || null,
            failover_enabled: res.failover_enabled === true,
            effective_chain: Array.isArray(res.effective_chain) ? res.effective_chain : null
          }
        } else {
          this.paymentAutoFailoverChain = null
          this.paymentRuleTestResult = null
          this.showError(new Error(res && res.message ? res.message : 'โหลดลำดับไม่สำเร็จ'))
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.loadingPaymentAutoFailoverChain = false
      }
    },
    showError (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || err.message || 'เกิดข้อผิดพลาด'
      this.$vs.notify({
        color: 'danger',
        title: 'เกิดข้อผิดพลาด',
        text: msg,
        position: 'top-right',
        time: 5000
      })
    },
    formatNumber (val) {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      if (isNaN(n)) return '-'
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    /** แสดงในช่องกรอก — ว่างถ้ายังไม่มีค่า */
    formatNumberInput (val) {
      if (val === null || val === undefined || val === '') return ''
      const n = Number(val)
      if (isNaN(n)) return ''
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    /** รับค่าที่พิมพ์ (มีคัมมา/ช่องว่าง) เป็นตัวเลข */
    parseFormattedNumberInput (raw) {
      if (raw === null || raw === undefined) return NaN
      const s = String(raw).trim().replace(/,/g, '').replace(/\s/g, '')
      if (s === '') return NaN
      const n = parseFloat(s)
      return n
    },
    /** หลัง blur — จัดรูปแบบ; ถ้าพิมพ์ไม่ถูกคืนข้อความเดิม */
    formatNumberFieldOnBlur (raw) {
      const s = raw === null || raw === undefined ? '' : String(raw).trim()
      if (s === '') return ''
      const n = this.parseFormattedNumberInput(s)
      if (isNaN(n)) return s
      return this.formatNumberInput(n)
    },
    getPaymentLabelByCode (code) {
      if (!code) return '-'
      const found = this.bankLists.find(item => item.code === code)
      return found ? (found.name_th || found.name_en || found.code) : code
    },
    async fetchBankLists () {
      try {
        const data = await getBankLists({ type: 'payment' })
        this.bankLists = Array.isArray(data) ? data : []
      } catch (err) {
        this.showError(err)
      }
    },
    async fetchPaymentRules () {
      this.loadingPaymentRules = true
      try {
        const params = this.paymentRulesActiveOnly ? { active_only: true } : {}
        const res = await getPaymentRules(params)
        if (!res || res.status !== true) {
          this.showError(new Error((res && res.message) || 'โหลด payment rules ไม่สำเร็จ'))
          this.paymentRules = []
          return
        }
        const list = res.data
        this.paymentRules = Array.isArray(list) ? this.sortPaymentRulesByPriority(list) : []
        this.paymentRuleShadowWarnings = res.shadow_warnings && typeof res.shadow_warnings === 'object' ? res.shadow_warnings : {}
      } catch (err) {
        this.showError(err)
        this.paymentRules = []
      } finally {
        this.loadingPaymentRules = false
      }
    },
    /** ทำ deposit_type ให้เป็นคีย์เดียวกันเสมอ — null/ว่าง จัดเข้ากลุ่ม «ไม่ระบุ» */
    depositTypeKey (val) {
      if (val === undefined || val === null) return DEPOSIT_TYPE_UNKNOWN
      const s = String(val).trim()
      return s === '' ? DEPOSIT_TYPE_UNKNOWN : s
    },
    depositTypeLabel (val) {
      const key = val === DEPOSIT_TYPE_ALL ? DEPOSIT_TYPE_ALL : this.depositTypeKey(val)
      if (key === DEPOSIT_TYPE_ALL) return 'ทั้งหมด'
      const meta = DEPOSIT_TYPE_META[key]
      return meta ? meta.label : key
    },
    depositTypeColor (val) {
      const meta = DEPOSIT_TYPE_META[this.depositTypeKey(val)]
      return meta ? meta.color : 'success'
    },
    /** ป้ายสั้นสำหรับในตาราง */
    depositTypeShortLabel (val) {
      const key = this.depositTypeKey(val)
      const meta = DEPOSIT_TYPE_META[key]
      return meta ? meta.short : key
    },
    depositTypeTextClass (val) {
      const meta = DEPOSIT_TYPE_META[this.depositTypeKey(val)]
      return meta ? meta.textClass : 'text-success'
    },
    isPaymentRulePriorityLocked (row) {
      if (row === null || row === undefined) return false
      return Number(row.priority) === PAYMENT_RULE_LOCK_PRIORITY
    },
    paymentRulesLockedCount () {
      return (this.paymentRules || []).filter(r => this.isPaymentRulePriorityLocked(r)).length
    },
    paymentRuleCanMoveUp (tr, trIdx) {
      if (this.isPaymentRuleReorderLocked) return false
      if (this.isPaymentRulePriorityLocked(tr)) return false
      return trIdx > 0
    },
    paymentRuleCanMoveDown (tr, trIdx) {
      if (this.isPaymentRuleReorderLocked) return false
      if (this.isPaymentRulePriorityLocked(tr)) return false
      const len = (this.paymentRules || []).length
      const nLocked = this.paymentRulesLockedCount()
      const lastUnlockedIdx = len - nLocked - 1
      return trIdx < lastUnlockedIdx
    },
    normalizePaymentRulesLocked999Order () {
      const list = this.paymentRules || []
      const unlocked = list.filter(r => !this.isPaymentRulePriorityLocked(r))
      const locked = list.filter(r => this.isPaymentRulePriorityLocked(r))
      this.paymentRules = [...unlocked, ...locked]
    },
    onPaymentRuleDragMove (evt) {
      if (this.isPaymentRuleReorderLocked) return false
      const dragged = evt.draggedContext && evt.draggedContext.element
      if (!dragged) return true
      if (this.isPaymentRulePriorityLocked(dragged)) return false
      const list = this.paymentRules || []
      const nLocked = this.paymentRulesLockedCount()
      if (nLocked === 0) return true
      const fi = evt.draggedContext.futureIndex
      if (fi === undefined || fi === null) return true
      return fi < list.length - nLocked
    },
    sortPaymentRulesByPriority (list) {
      const copy = [...list]
      const unlocked = copy.filter(r => !this.isPaymentRulePriorityLocked(r))
      const locked = copy.filter(r => this.isPaymentRulePriorityLocked(r))
      const byPriority = (a, b) => {
        const pa = Number(a && a.priority)
        const pb = Number(b && b.priority)
        const aOk = !isNaN(pa)
        const bOk = !isNaN(pb)
        if (aOk && bOk && pa !== pb) return pa - pb
        if (aOk && !bOk) return -1
        if (!aOk && bOk) return 1
        const ida = a && a.id !== undefined && a.id !== null ? Number(a.id) : 0
        const idb = b && b.id !== undefined && b.id !== null ? Number(b.id) : 0
        return ida - idb
      }
      unlocked.sort(byPriority)
      locked.sort(byPriority)
      return [...unlocked, ...locked]
    },
    onPaymentRuleDraggableEnd () {
      if (this.isPaymentRulesDepositTypeFiltered) return
      this.normalizePaymentRulesLocked999Order()
      this.paymentRules = [...this.paymentRules]
      this.persistPaymentRulesOrder()
    },
    movePaymentRuleRow (tr, delta) {
      if (this.isPaymentRuleReorderLocked) return
      if (this.isPaymentRulePriorityLocked(tr)) return
      const idx = this.paymentRules.findIndex(r => r.id === tr.id)
      if (idx < 0) return
      const n = idx + delta
      if (n < 0 || n >= this.paymentRules.length) return
      const lastUnlockedIdx = this.paymentRules.length - this.paymentRulesLockedCount() - 1
      if (n > lastUnlockedIdx) return
      const list = [...this.paymentRules]
      const t = list[idx]
      list.splice(idx, 1)
      list.splice(n, 0, t)
      this.paymentRules = list
      this.normalizePaymentRulesLocked999Order()
      this.paymentRules = [...this.paymentRules]
      this.persistPaymentRulesOrder()
    },
    async persistPaymentRulesOrder () {
      this.reorderingPaymentRules = true
      try {
        this.normalizePaymentRulesLocked999Order()
        const list = [...this.paymentRules]
        const unlocked = list.filter(r => !this.isPaymentRulePriorityLocked(r))
        const locked = list.filter(r => this.isPaymentRulePriorityLocked(r))
        if (unlocked.length > PAYMENT_RULE_LOCK_PRIORITY) {
          this.showError(new Error(`มีกฎที่ปรับลำดับได้เกิน ${PAYMENT_RULE_LOCK_PRIORITY} รายการ — priority ต้องอยู่ 0–${PAYMENT_RULE_LOCK_PRIORITY - 1} เท่านั้น (เว้นกฎล็อก ${PAYMENT_RULE_LOCK_PRIORITY})`))
          await this.fetchPaymentRules()
          return
        }
        const tasks = []
        unlocked.forEach((r, i) => {
          const cur = Number(r.priority)
          if (isNaN(cur) || cur !== i) {
            tasks.push(updatePaymentRule(r.id, { priority: i }))
          }
        })
        locked.forEach((r) => {
          const cur = Number(r.priority)
          if (isNaN(cur) || cur !== PAYMENT_RULE_LOCK_PRIORITY) {
            tasks.push(updatePaymentRule(r.id, { priority: PAYMENT_RULE_LOCK_PRIORITY }))
          }
        })
        if (tasks.length) {
          const results = await Promise.all(tasks)
          const bad = results.find(res => !res || res.status !== true)
          if (bad) {
            this.showError(new Error((bad && bad.message) || 'บันทึกลำดับไม่สำเร็จ'))
            await this.fetchPaymentRules()
            return
          }
          await this.fetchPaymentRules()
          this.$vs.notify({ color: 'success', title: 'บันทึกลำดับกฎแล้ว', position: 'top-right' })
        }
      } catch (err) {
        this.showError(err)
        await this.fetchPaymentRules()
      } finally {
        this.reorderingPaymentRules = false
      }
    },
    toggleConditionsExpand (id) {
      const key = String(id)
      const cur = !!this.expandedConditions[key]
      this.$set(this.expandedConditions, key, !cur)
    },
    formatConditionsJson (conditions) {
      if (conditions === undefined || conditions === null) return '—'
      try {
        return JSON.stringify(conditions, null, 2)
      } catch (_) {
        return String(conditions)
      }
    },
    conditionsPreview (tr) {
      let fromApi = ''
      if (
        tr &&
        tr.condition !== undefined &&
        tr.condition !== null &&
        String(tr.condition).trim() !== ''
      ) {
        fromApi = String(tr.condition).trim()
      }
      if (fromApi) return this.truncateText(fromApi, 72)
      const name = tr && tr.name ? String(tr.name) : ''
      if (name) return this.truncateText(name, 72)
      return this.truncateText(this.formatConditionsJson(tr.conditions), 72)
    },
    truncateText (s, max) {
      if (s === null || s === undefined) return '—'
      const str = String(s)
      if (str.length <= max) return str
      return `${str.slice(0, max)}…`
    },
    formatDateTime (val) {
      if (val === null || val === undefined || val === '') return '—'
      const d = new Date(val)
      if (isNaN(d.getTime())) return String(val)
      return d.toLocaleString('th-TH')
    },
    parseConditionsObject () {
      const raw = (this.paymentRuleForm.conditionsText || '').trim()
      if (!raw) return { ok: true, value: undefined }
      let parsed = null
      try {
        parsed = JSON.parse(raw)
      } catch (e) {
        return { ok: false, error: 'รูปแบบ JSON ไม่ถูกต้อง — ตรวจวงเล็บ หรือเครื่องหมายคำพูด' }
      }
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ok: false, error: 'JSON ต้องเป็นก้อนวงเล็บปีกกา { … } ไม่ใช่รายการใน [ ] อย่างเดียว' }
      }
      return { ok: true, value: parsed }
    },
    getConditionsPayloadParsed () {
      if (this.paymentRuleConditionsEditor === 'builder') {
        const b = this.buildConditionsFromBuilder()
        if (b.error) {
          return { ok: false, error: b.error }
        }
        return { ok: true, value: b.value }
      }
      return this.parseConditionsObject()
    },
    async fetchPaymentRuleContextKeys () {
      this.loadingPaymentRuleContextKeys = true
      try {
        const res = await getPaymentRuleContextKeys({ active_only: true })
        let list = []
        if (res && res.status === true && Array.isArray(res.data)) {
          list = res.data
        } else if (Array.isArray(res)) {
          list = res
        } else if (res && Array.isArray(res.data)) {
          list = res.data
        }
        this.paymentRuleContextKeys = list.filter(c => c && c.is_active !== false)
      } catch (_) {
        this.paymentRuleContextKeys = []
      } finally {
        this.loadingPaymentRuleContextKeys = false
      }
    },
    createEmptyConditionRow () {
      return {
        _uid: `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        context_key: '',
        num_gte: '',
        num_lte: '',
        num_gt: '',
        num_lt: '',
        num_eq: '',
        str_value: '',
        bank_preset: '',
        bank_other: ''
      }
    },
    contextMetaForRow (row) {
      if (!row || !row.context_key) return null
      return this.paymentRuleContextKeys.find(c => c.context_key === row.context_key) || null
    },
    contextKeyOptionText (ck) {
      if (!ck) return ''
      const base = ck.label_th || ck.context_key
      const t = ck.value_type
      if (t === 'number') return `${base}`
      if (t === 'boolean') return `${base}`
      if (t === 'string') return `${base}`
      return base
    },
    parseConditionNum (v) {
      if (v === '' || v === null || v === undefined) return null
      const n = this.parseFormattedNumberInput(v)
      return isNaN(n) ? null : n
    },
    /**
     * ตัวอย่างอ่านง่ายแบบเรียลไทม์สำหรับช่องตัวเลข (เช่น ขั้นต่ำ+สูงสุด → "100 – 200")
     */
    getNumberConditionPreview (row) {
      if (!row) return null
      const gte = this.parseConditionNum(row.num_gte)
      const lte = this.parseConditionNum(row.num_lte)
      const gt = this.parseConditionNum(row.num_gt)
      const lt = this.parseConditionNum(row.num_lt)
      const eq = this.parseConditionNum(row.num_eq)
      if (gte === null && lte === null && gt === null && lt === null && eq === null) return null

      const fmt = (n) => this.formatNumberInput(n)

      let rangeLine = null
      if (gte !== null && lte !== null) {
        rangeLine = `${fmt(gte)} – ${fmt(lte)}`
      }

      const chunks = []
      if (gte !== null && lte !== null) {
        chunks.push(`ช่วงตั้งแต่ ${fmt(gte)} ถึง ${fmt(lte)} (ไม่ต่ำกว่า ${fmt(gte)} และไม่เกิน ${fmt(lte)})`)
      } else {
        if (gte !== null) chunks.push(`ไม่ต่ำกว่า ${fmt(gte)}`)
        if (lte !== null) chunks.push(`ไม่เกิน ${fmt(lte)}`)
      }
      if (gt !== null) chunks.push(`มากกว่า ${fmt(gt)}`)
      if (lt !== null) chunks.push(`น้อยกว่า ${fmt(lt)}`)
      if (eq !== null) chunks.push(`เท่ากับ ${fmt(eq)} พอดี`)

      return {
        rangeLine,
        detail: chunks.join(' · ')
      }
    },
    numberPreviewWrap (row) {
      const p = this.getNumberConditionPreview(row)
      return p ? [p] : []
    },
    summarizeNumberPredicate (label, contextKey, obj) {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return label
      const has = (x) => x !== undefined && x !== null
      const df = (x) => this.formatNumberInput(Number(x))
      const gte = obj.gte
      const lte = obj.lte
      const gt = obj.gt
      const lt = obj.lt
      const eq = obj.eq
      if (has(gte) && has(lte) && !has(gt) && !has(lt) && !has(eq)) {
        return `${label} ช่วง ${df(gte)}–${df(lte)}`
      }
      if (has(gte) && !has(lte) && !has(gt) && !has(lt) && !has(eq)) {
        if (contextKey === 'deposit_amount') {
          return `${label} ขั้นต่ำ ${df(gte)}`
        }
        if (contextKey === 'total_deposit') {
          return `${label} อย่างน้อย ${df(gte)}`
        }
        return `${label} ไม่ต่ำกว่า ${df(gte)}`
      }
      if (has(lte) && !has(gte) && !has(gt) && !has(lt) && !has(eq)) {
        return `${label} ไม่เกิน ${df(lte)}`
      }
      if (has(eq) && !has(gte) && !has(lte) && !has(gt) && !has(lt)) {
        return `${label} เท่ากับ ${df(eq)}`
      }
      const parts = []
      if (has(gte)) parts.push(`ไม่ต่ำกว่า ${df(gte)}`)
      if (has(lte)) parts.push(`ไม่เกิน ${df(lte)}`)
      if (has(gt)) parts.push(`มากกว่า ${df(gt)}`)
      if (has(lt)) parts.push(`น้อยกว่า ${df(lt)}`)
      if (has(eq)) parts.push(`เท่ากับ ${df(eq)}`)
      return parts.length ? `${label} ${parts.join(' และ ')}` : label
    },
    humanPhraseFromLeafValue (contextKey, val, meta) {
      const label = (meta && meta.label_th) ? meta.label_th : contextKey
      if (typeof val === 'boolean') {
        return `${label} ต้องเป็น${val ? 'ใช่' : 'ไม่ใช่'}`
      }
      if (typeof val === 'string' || typeof val === 'number') {
        if (contextKey === 'bank_code') {
          return `${label} เป็น ${val}`
        }
        return `${label} ตรงกับ ${val}`
      }
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        return this.summarizeNumberPredicate(label, contextKey, val)
      }
      return `${label}`
    },
    humanSummaryFromConditionsObject (conditions) {
      if (!conditions || typeof conditions !== 'object' || Array.isArray(conditions)) return null
      const keys = Object.keys(conditions)
      if (keys.length !== 1) {
        return 'เช็คจาก (โครงสร้างพิเศษ — ดูรายละเอียดใน JSON)'
      }
      const op = keys[0]
      if (op !== 'AND' && op !== 'OR') return null
      const arr = conditions[op]
      if (!Array.isArray(arr)) return null
      if (arr.length === 0) {
        return 'ยังไม่มีรายการเช็คใน JSON'
      }
      const pieces = []
      for (let i = 0; i < arr.length; i++) {
        const item = arr[i]
        if (!item || typeof item !== 'object' || Array.isArray(item)) continue
        const k = Object.keys(item)[0]
        const meta = this.paymentRuleContextKeys.find(c => c.context_key === k)
        pieces.push(this.humanPhraseFromLeafValue(k, item[k], meta))
      }
      if (pieces.length === 0) return null
      const glue = op === 'OR' ? ' หรือ ' : ' และ '
      return `${pieces.join(glue)}`
    },
    humanSummaryForConditionRow (row) {
      const meta = this.contextMetaForRow(row)
      if (!row.context_key || !meta) return null
      const leaf = this.conditionRowToLeaf(row)
      if (leaf.error) return null
      const key = row.context_key
      return this.humanPhraseFromLeafValue(key, leaf.obj[key], meta)
    },
    humanSummaryFromBuilderState () {
      const rows = this.conditionBuilderRows || []
      if (rows.length === 0) {
        return {
          show: true,
          line: 'ยังไม่มีข้อที่เช็ค — กด «เพิ่มเงื่อนไขอีกข้อ»',
          incomplete: 1
        }
      }
      const pieces = []
      let incomplete = 0
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        const s = this.humanSummaryForConditionRow(row)
        if (s) {
          pieces.push(s)
        } else {
          incomplete++
          if (row.context_key) {
            pieces.push(`(ข้อที่ ${i + 1} ยังกรอกค่าไม่ครบ)`)
          } else {
            pieces.push(`(ข้อที่ ${i + 1} ยังไม่ได้เลือกเรื่องที่จะเช็ค)`)
          }
        }
      }
      const glue = this.conditionBuilderRoot === 'OR' ? ' หรือ ' : ' และ '
      return {
        show: true,
        line: `${pieces.join(glue)}`,
        incomplete
      }
    },
    setPaymentRuleConditionsEditor (mode) {
      if (mode === 'json' && this.paymentRuleConditionsEditor === 'builder') {
        const b = this.buildConditionsFromBuilder()
        if (!b.error && b.value) {
          this.paymentRuleForm.conditionsText = JSON.stringify(b.value, null, 2)
        }
      }
      if (mode === 'builder' && this.paymentRuleConditionsEditor === 'json') {
        const parsed = this.parseConditionsObject()
        if (parsed.ok && parsed.value) {
          const ok = this.hydrateConditionBuilderFromParsed(parsed.value)
          if (!ok) {
            this.builderHydrateFailed = true
            this.$vs.notify({
              color: 'warning',
              title: 'สลับมาหน้าฟอร์มไม่ได้',
              text: 'ข้อความ JSON ชุดนี้ซับซ้อนเกินกว่าฟอร์มจะแปลง — แก้ในโหมดผู้ดูแลต่อ หรือให้ทีมเทคนิคช่วย',
              position: 'top-right',
              time: 5000
            })
            return
          }
          this.builderHydrateFailed = false
        }
      }
      this.paymentRuleConditionsEditor = mode
    },
    addConditionRow () {
      this.conditionBuilderRows.push(this.createEmptyConditionRow())
    },
    removeConditionRow (idx) {
      this.conditionBuilderRows.splice(idx, 1)
    },
    moveConditionRow (idx, delta) {
      const n = idx + delta
      if (n < 0 || n >= this.conditionBuilderRows.length) return
      const rows = [...this.conditionBuilderRows]
      const t = rows[idx]
      rows[idx] = rows[n]
      rows[n] = t
      this.conditionBuilderRows = rows
    },
    buildConditionsFromBuilder () {
      const rows = this.conditionBuilderRows || []
      if (rows.length === 0) {
        return { error: 'กด «เพิ่มเงื่อนไขอีกข้อ» อย่างน้อย 1 ครั้ง หรือสลับไปโหมดผู้ดูแล (JSON)', value: undefined }
      }
      const parts = []
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        if (!row.context_key) {
          return { error: `ข้อที่ ${i + 1}: ยังไม่ได้เลือกว่าจะเช็คจากเรื่องไหน` }
        }
        const leaf = this.conditionRowToLeaf(row)
        if (leaf.error) return { error: `ข้อที่ ${i + 1}: ${leaf.error}` }
        parts.push(leaf.obj)
      }
      const root = this.conditionBuilderRoot === 'OR' ? 'OR' : 'AND'
      return { value: { [root]: parts } }
    },
    conditionRowToLeaf (row) {
      const meta = this.contextMetaForRow(row)
      const key = row.context_key
      if (!meta) {
        return { error: 'รายการเรื่องที่เช็คได้ยังไม่ตรงกับข้อมูล — กดโหลดรายการใหม่ หรือแจ้งทีมระบบ' }
      }
      if (meta.value_type === 'number') {
        const o = {}
        const addNum = (k, v) => {
          if (v === '' || v === null || v === undefined) return
          const n = this.parseFormattedNumberInput(v)
          if (isNaN(n)) return
          o[k] = n
        }
        addNum('gte', row.num_gte)
        addNum('lte', row.num_lte)
        addNum('gt', row.num_gt)
        addNum('lt', row.num_lt)
        addNum('eq', row.num_eq)
        if (Object.keys(o).length === 0) {
          return { error: 'ใส่ตัวเลขอย่างน้อยหนึ่งช่อง (เช่น ขั้นต่ำ / สูงสุด / เท่ากับ)' }
        }
        return { obj: { [key]: o } }
      }
      if (meta.value_type === 'boolean') {
        return { obj: { [key]: true } }
      }
      if (key === 'bank_code') {
        const preset = (row.bank_preset || '').trim()
        if (!preset) {
          return { error: 'กดเลือกธนาคาร หรือเลือก «อื่น ๆ» แล้วพิมพ์รหัส' }
        }
        const codeVal = preset === '__other__' ? (row.bank_other || '').trim() : preset
        if (!codeVal) {
          return { error: 'เมื่อเลือก «อื่น ๆ» ต้องพิมพ์รหัสธนาคารในช่องด้านล่าง' }
        }
        return { obj: { bank_code: codeVal } }
      }
      const s = (row.str_value || '').trim()
      if (!s) {
        return { error: 'พิมพ์ค่าที่ต้องตรงในช่องข้อความ' }
      }
      return { obj: { [key]: s } }
    },
    parseSingleLeafToRow (leafObj) {
      if (!leafObj || typeof leafObj !== 'object' || Array.isArray(leafObj)) return null
      const keys = Object.keys(leafObj)
      if (keys.length !== 1) return null
      const k = keys[0]
      const val = leafObj[k]
      const meta = this.paymentRuleContextKeys.find(c => c.context_key === k)
      const row = this.createEmptyConditionRow()
      row.context_key = k
      if (meta && meta.value_type === 'number') {
        if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
          row.num_gte = val.gte !== undefined && val.gte !== null ? this.formatNumberInput(val.gte) : ''
          row.num_lte = val.lte !== undefined && val.lte !== null ? this.formatNumberInput(val.lte) : ''
          row.num_gt = val.gt !== undefined && val.gt !== null ? this.formatNumberInput(val.gt) : ''
          row.num_lt = val.lt !== undefined && val.lt !== null ? this.formatNumberInput(val.lt) : ''
          row.num_eq = val.eq !== undefined && val.eq !== null ? this.formatNumberInput(val.eq) : ''
        } else if (typeof val === 'number' && !isNaN(val)) {
          row.num_eq = this.formatNumberInput(val)
        } else {
          return null
        }
        return row
      }
      if (meta && meta.value_type === 'boolean') {
        return row
      }
      if (k === 'bank_code') {
        const s = (val !== undefined && val !== null) ? String(val) : ''
        const presetValues = RULE_BANK_CODE_OPTIONS.map(o => o.value).filter(v => v !== '__other__')
        if (presetValues.includes(s)) {
          row.bank_preset = s
        } else {
          row.bank_preset = '__other__'
          row.bank_other = s
        }
        return row
      }
      if (typeof val === 'string' || typeof val === 'number') {
        row.str_value = String(val)
        return row
      }
      return null
    },
    hydrateConditionBuilderFromParsed (conditions) {
      if (!conditions || typeof conditions !== 'object' || Array.isArray(conditions)) return false
      const keys = Object.keys(conditions)
      if (keys.length !== 1 || (keys[0] !== 'AND' && keys[0] !== 'OR')) return false
      const op = keys[0]
      const arr = conditions[op]
      if (!Array.isArray(arr)) return false
      const rows = []
      for (let i = 0; i < arr.length; i++) {
        const item = arr[i]
        if (item && typeof item === 'object' && !Array.isArray(item) && item.OR !== undefined && Array.isArray(item.OR)) {
          return false
        }
        if (item && typeof item === 'object' && !Array.isArray(item) && item.AND !== undefined) {
          return false
        }
        const row = this.parseSingleLeafToRow(item)
        if (!row) return false
        rows.push(row)
      }
      this.conditionBuilderRoot = op
      this.conditionBuilderRows = rows.length ? rows : [this.createEmptyConditionRow()]
      return true
    },
    hydrateConditionBuilderFromRuleConditions (conditions) {
      this.builderHydrateFailed = false
      if (conditions === undefined || conditions === null) {
        this.paymentRuleConditionsEditor = 'builder'
        this.conditionBuilderRoot = 'AND'
        this.conditionBuilderRows = [this.createEmptyConditionRow()]
        return true
      }
      if (typeof conditions === 'object' && !Array.isArray(conditions) && Object.keys(conditions).length === 0) {
        this.paymentRuleConditionsEditor = 'builder'
        this.conditionBuilderRoot = 'AND'
        this.conditionBuilderRows = [this.createEmptyConditionRow()]
        return true
      }
      const ok = this.hydrateConditionBuilderFromParsed(conditions)
      if (!ok) {
        this.builderHydrateFailed = true
        this.paymentRuleConditionsEditor = 'json'
        this.conditionBuilderRoot = 'AND'
        this.conditionBuilderRows = [this.createEmptyConditionRow()]
        return false
      }
      this.paymentRuleConditionsEditor = 'builder'
      return true
    },
    openPaymentRuleAdd () {
      this.paymentRuleFormMode = 'add'
      this.resetPaymentRuleForm()
      this.paymentRuleForm.is_active = true
      this.fetchPaymentRuleContextKeys()
      this.showPaymentRuleModal = true
    },
    async openPaymentRuleEdit (row) {
      if (!row || row.id === undefined || row.id === null) return
      this.loadingPaymentRuleDetail = true
      this.paymentRuleFormErrors = {}
      await this.fetchPaymentRuleContextKeys()
      try {
        const res = await getPaymentRule(row.id)
        if (!res || res.status !== true) {
          this.showError(new Error((res && res.message) || 'ไม่พบ rule'))
          return
        }
        const rule = res.data || {}
        this.paymentRuleFormMode = 'edit'
        this.paymentRuleForm = {
          id: rule.id,
          name: this.paymentRuleNameForEditForm(rule),
          payment_codes: this.paymentRuleCodes(rule),
          is_active: !!rule.is_active,
          conditionsText: (rule.conditions !== undefined && rule.conditions !== null) ? JSON.stringify(rule.conditions, null, 2) : '{}'
        }
        this.hydrateConditionBuilderFromRuleConditions(rule.conditions)
        this.showPaymentRuleModal = true
      } catch (err) {
        this.showError(err)
      } finally {
        this.loadingPaymentRuleDetail = false
      }
    },
    resetPaymentRuleForm () {
      this.paymentRuleForm = {
        id: null,
        name: '',
        payment_codes: [],
        is_active: true,
        conditionsText: '{}'
      }
      this.paymentRuleFormErrors = {}
      this.paymentRuleConditionsEditor = 'builder'
      this.conditionBuilderRoot = 'AND'
      this.conditionBuilderRows = [this.createEmptyConditionRow()]
      this.builderHydrateFailed = false
    },
    /** ชื่อที่โชว์ในช่องตอนแก้ไข: ใช้ name ก่อน ถ้าไม่มีใช้ description / condition จาก API */
    paymentRuleNameForEditForm (rule) {
      if (!rule || typeof rule !== 'object') return ''
      const pick = (v) => {
        if (v === undefined || v === null) return ''
        const s = String(v).trim()
        return s
      }
      return pick(rule.name) || pick(rule.description) || pick(rule.condition)
    },
    validatePaymentRuleForm () {
      this.paymentRuleFormErrors = {}
      const cond = this.getConditionsPayloadParsed()
      if (!cond.ok) {
        this.paymentRuleFormErrors.conditions = cond.error
        return false
      }
      if (cond.ok && cond.value !== undefined) {
        this.paymentRuleForm.conditionsText = JSON.stringify(cond.value, null, 2)
      }
      if (!this.paymentRuleForm.payment_codes.length) {
        this.paymentRuleFormErrors.payment_code = 'กรุณาเลือกช่องทางรับเงิน'
        return false
      }
      return true
    },
    /** ลำดับค่ายของกฎ (API ส่ง payment_codes มาเสมอ — แถวจาก API เก่าใช้ payment_code) */
    paymentRuleCodes (rule) {
      if (!rule) return []
      if (Array.isArray(rule.payment_codes) && rule.payment_codes.length) return rule.payment_codes
      return rule.payment_code ? [String(rule.payment_code)] : []
    },
    addPaymentRuleFormCode (code) {
      if (!code || this.paymentRuleForm.payment_codes.includes(code)) return
      this.paymentRuleForm.payment_codes.push(code)
      this.paymentRuleFormErrors = { ...this.paymentRuleFormErrors, payment_code: undefined }
    },
    removePaymentRuleFormCode (index) {
      this.paymentRuleForm.payment_codes.splice(index, 1)
    },
    movePaymentRuleFormCode (index, dir) {
      const list = this.paymentRuleForm.payment_codes.slice()
      const to = index + dir
      if (to < 0 || to >= list.length) return
      const tmp = list[index]
      list[index] = list[to]
      list[to] = tmp
      this.paymentRuleForm.payment_codes = list
    },
    buildPaymentRulePayload (forCreate) {
      const body = {}
      const manualName = (this.paymentRuleForm.name || '').trim()
      /** เหมือนข้อความในกล่อง «สรุปก่อนกดยืนยัน» — ใช้เป็นชื่อเมื่อช่องชื่อว่าง */
      const summaryName = (this.paymentRuleSystemDefaultNamePreview || '').trim()
      if (manualName) {
        body.name = manualName
      } else if (summaryName) {
        body.name = summaryName
      } else if (!forCreate) {
        body.name = null
      }
      body.is_active = !!this.paymentRuleForm.is_active
      const codes = this.paymentRuleForm.payment_codes.slice()
      if (codes.length) body.payment_codes = codes
      const parsed = this.getConditionsPayloadParsed()
      if (parsed.ok && parsed.value !== undefined) body.conditions = parsed.value
      const autoDesc = (this.paymentRuleSystemDefaultNamePreview || '').trim()
      body.description = autoDesc || null
      const nameForCreate = manualName || summaryName
      if (forCreate) {
        return {
          payment_codes: codes,
          is_active: body.is_active,
          priority: PAYMENT_RULE_DEFAULT_CREATE_PRIORITY,
          description: body.description,
          ...(nameForCreate ? { name: nameForCreate } : {}),
          ...(parsed.ok && parsed.value !== undefined ? { conditions: parsed.value } : {})
        }
      }
      return body
    },
    async submitPaymentRuleForm () {
      if (!this.validatePaymentRuleForm()) return
      this.savingPaymentRule = true
      try {
        if (this.paymentRuleFormMode === 'add') {
          const payload = this.buildPaymentRulePayload(true)
          if (!payload.payment_codes.length) {
            this.paymentRuleFormErrors.payment_code = 'กรุณาเลือกช่องทางรับเงิน'
            return
          }
          const res = await createPaymentRule(payload)
          if (!res || res.status !== true) {
            this.showError(new Error((res && res.message) || 'สร้าง rule ไม่สำเร็จ'))
            return
          }
          this.showPaymentRuleModal = false
          this.resetPaymentRuleForm()
          await this.fetchPaymentRules()
          this.$vs.notify({ color: 'success', title: 'สร้าง payment rule สำเร็จ', position: 'top-right' })
        } else {
          const id = this.paymentRuleForm.id
          const body = this.buildPaymentRulePayload(false)
          const keys = ['name', 'is_active', 'payment_codes', 'conditions', 'description']
          const hasAny = keys.some(k => Object.prototype.hasOwnProperty.call(body, k))
          if (!hasAny) {
            this.showError(new Error('ไม่มีฟิลด์ที่จะอัปเดต'))
            return
          }
          const res = await updatePaymentRule(id, body)
          if (!res || res.status !== true) {
            this.showError(new Error((res && res.message) || 'บันทึกไม่สำเร็จ'))
            return
          }
          this.showPaymentRuleModal = false
          this.resetPaymentRuleForm()
          await this.fetchPaymentRules()
          this.$vs.notify({ color: 'success', title: 'บันทึก payment rule สำเร็จ', position: 'top-right' })
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.savingPaymentRule = false
      }
    },
    async togglePaymentRuleActive (row) {
      const newActive = !row.is_active
      try {
        const res = await updatePaymentRule(row.id, { is_active: newActive })
        if (!res || res.status !== true) {
          row.is_active = !newActive
          this.showError(new Error((res && res.message) || 'อัปเดตสถานะไม่สำเร็จ'))
          return
        }
        await this.fetchPaymentRules()
        this.$vs.notify({
          color: 'success',
          title: newActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน',
          position: 'top-right'
        })
      } catch (err) {
        row.is_active = !newActive
        this.showError(err)
      }
    }
  }
}
</script>

<style>
.payment-rule-codes {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 220px;
}
.payment-rule-code-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  border: 1px solid rgba(var(--vs-primary), 0.35);
  opacity: 0.8;
}
.payment-rule-code-chip--first {
  background: rgba(var(--vs-primary), 0.15);
  opacity: 1;
  font-weight: 600;
}
.payment-rule-code-chip__no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  margin-right: 4px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: rgba(var(--vs-primary), 1);
}
.payment-rule-shadow {
  line-height: 1.35;
  max-width: 28rem;
  white-space: normal;
}
.payment-rule-shadow--shadowed {
  color: rgba(var(--vs-danger), 1);
  font-weight: 600;
}
.payment-rule-shadow--partial,
.payment-rule-shadow--tie {
  color: rgba(var(--vs-warning), 1);
}
.payment-rule-shadow--backup {
  color: #9e9e9e;
}
.payment-rule-test-result > div {
  margin-top: 2px;
}
/* vs-popup ถูกย้าย DOM — ใช้ global class */
.popup-payment-rule-wide .vs-popup--content {
  min-width: min(720px, 94vw);
  max-width: 94vw;
}

/* ข้อความในกล่องเงื่อนไข / JSON — โหมดมืดไม่ให้ตัวอักษรจมกับพื้นหลัง */
.theme-dark .conditions-json,
.theme-dark .payment-rule-conditions-textarea {
  color: #e2e8f0;
}
.theme-dark .payment-rule-conditions-textarea::placeholder {
  color: rgba(226, 232, 240, 0.45);
}

.theme-dark .condition-preview-range,
.theme-dark .condition-preview-detail {
  color: #e2e8f0;
}

.theme-dark .payment-rule-pre-summary-line {
  color: #f1f5f9;
}

/* ประเภทฝากในตาราง — จุดสี + ข้อความ ไม่ใช้ chip ให้แถวดูโปร่ง */
.deposit-type-tag {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}
.deposit-type-tag__dot {
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.payment-rule-drag-handle {
  cursor: grab;
}
.payment-rule-drag-handle:hover {
  background: rgba(var(--vs-primary), .08);
}
.payment-rule-drag-handle:active {
  cursor: grabbing;
}
/* vuedraggable / Sortable — แถวขณะลาก */
.sortable-ghost {
  opacity: 0.45;
}
.sortable-drag {
  opacity: 1;
}

.payment-rule-tr--locked .vs-table--td {
  background-color: rgba(var(--vs-warning), .06);
}
</style>
